import { memo, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isArray, noop, isEmpty, isNil } from 'lodash';
// Import Types
import type { ListHeaderConfig } from 'types';
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import Components
import DataTable, { type DataTableRefActions } from 'components/common/DataTable';
import ReportActionsButtons from './ReportActionsButtons';
import ReportActionsModals from './ReportActionsModals';
// Import Hooks
import useToaster from 'hooks/UseToaster';
import useGoToRoute from 'hooks/GoToRoute';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Context
import { useModalActionsContext } from 'context/modalActionsContext';
// Import Services
import { ReportHttpService } from 'services/http';
// Import Helpers
import { getPageNumber } from 'services/Helpers';
// Import Configs
import { DataTableColumnsConfigs, modalsStateSetDictKeys } from './config';
// Import Constants
import { TargetTypeEnum, l, ReportTypesLabels, ModalsActions, AlertTypes } from 'constants/common';
import { RoutesList } from 'routes';
// Import Styles
import 'assets/scss/reportListPage.scss';

const {
    REPORT_EXECUTED_LIST,
    REPORT_EDIT,
    REPORT_CLONE,
    REPORT_SCHEDULE,
    REPORT_EXECUTE,
    REPORT_COMPOSITE_EDIT,
    REPORT_COMPOSITE_CLONE,
    REPORT_CUSTOMER_JOURNEY_EDIT,
    REPORT_CUSTOMER_JOURNEY_CLONE,
    REPORT_PREDEFINED_CLONE,
} = RoutesList;

export interface ReportListProps {
    getRadioSelectedRow?: (row: AnyObject) => void;
    isShowVerticalDots?: boolean;
    withManageLabels?: boolean;
    isViewMode?: boolean;
    getHeaderActions?: () => React.ReactNode;
    rowsUpdateCallback?: (rows: AnyObject[]) => void;
    isHasRowMultiSelect?: boolean;
    isHasRowRadioSelect?: boolean;
    isHasBulkActions?: boolean;
    isShowColumnChooser?: boolean;
    defaultRadioSelectedRow?: AnyObject;
    getDragAcceptType?: (row: string) => string;
    CustomPreview?: React.ReactNode;
    dragDisable?: boolean;
    tableKey?: string;
    withQueryParams?: boolean;
    dataTableClassNames?: string;
    getTitle?: (row?: AnyObject) => string;
    isHasLeftHeaderActions?: boolean;
    headerLeftActions?: React.ReactNode;
    listFilters: UseListFiltersReturnValue;
    /** Compact single-row header; when set, `getHeaderActions` is ignored. */
    headerConfig?: ListHeaderConfig;
}

export interface ReportListRef {
    resetSelectedRows: () => void;
    reset: () => void;
}

const ReportList = forwardRef<ReportListRef, ReportListProps>((props, ref) => {
    const {
        tableKey = 'reportListDataTable',
        getHeaderActions = noop,
        dataTableClassNames = 'default-height',
        withManageLabels = false,
        isShowVerticalDots = true,
        isShowColumnChooser = true,
        isHasRowMultiSelect = false,
        isHasBulkActions = false,
        withQueryParams = false,
        getTitle = () => '',
        isViewMode = false,
        rowsUpdateCallback = noop,
        isHasRowRadioSelect = false,
        getRadioSelectedRow = noop,
        defaultRadioSelectedRow = null,
        getDragAcceptType = () => '',
        dragDisable = false,
        CustomPreview,
        isHasLeftHeaderActions,
        headerLeftActions,
        listFilters,
        headerConfig,
    } = props;
    const { search } = useLocation();
    const { t } = useTranslation();
    const { showToaster } = useToaster();
    const { getRoutePath, goToPath } = useGoToRoute();
    const { doPostRequest } = useRequest();
    const { modalActionsRef } = useModalActionsContext();

    const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

    const [isReportListLoading, setIsReportListLoading] = useState(false);
    const [reportListData, setReportListData] = useState([]);
    const [reportListDataCount, setReportListDataCount] = useState(0);

    const [isDeleteModalOpenedState, setIsDeleteModalOpenedState] = useState(false);
    const [isStopScheduleModalOpenedState, setIsStopScheduleModalOpenedState] = useState(false);
    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(false);
    const [isExecuteModalOpenedState, setIsExecuteModalOpenedState] = useState(false);
    const [isUsedInConfirmationModalOpenedState, setIsUsedInConfirmationModalOpenedState] = useState(false);
    const [isArchiveModalOpenedState, setIsArchiveModalOpenedState] = useState(false);
    const [isUnarchiveModalOpenedState, setIsUnarchiveModalOpenedState] = useState(false);
    const [openedModalData, setOpenedModalData] = useState({});

    const { partnerDateTimeFormat } = usePartnerDateFormats();
    const { columns } = DataTableColumnsConfigs(t, partnerDateTimeFormat);
    const dataTableRef = useRef<DataTableRefActions>(null);

    const { getReportsRequest, getReportsByReport } = useCancelableRequests(
        {
            getReportsRequest: ReportHttpService.getReportList,
            getReportsByReport: ReportHttpService.getReportsByReport,
        },
        { takeLatest: ['getReportsRequest'] },
    );

    const goToRouteQueryParams = useQueryParams();

    const getReports = useCallback(
        (filter = baseRequestBody) => {
            setIsReportListLoading(true);
            doPostRequest(getReportsRequest.request, {
                requestBody: filter,
                successCallback: ({ Data, Count }) => {
                    const rows = getTableRows(Data || []);
                    setReportListData(rows);
                    setReportListDataCount(Count);
                    setIsReportListLoading(false);
                    rowsUpdateCallback(rows);
                },
                errorCallback: () => {
                    setIsReportListLoading(false);
                },
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseRequestBody],
    );

    const getEditReportPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId };

        const route =
            ReportType === ReportTypesLabels.Report
                ? REPORT_EDIT
                : ReportType === ReportTypesLabels.CompositeReport
                  ? REPORT_COMPOSITE_EDIT
                  : REPORT_CUSTOMER_JOURNEY_EDIT;

        return getRoutePath(route, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditReport = (report) => {
        const path = getEditReportPath(report);
        goToPath(path);
    };

    // Actions part
    const deleteReportAction = (report) => {
        setOpenedModalData(report);
        setIsDeleteModalOpenedState(true);
    };

    const getExecuteReportPath = (report) => {
        const { ReportId, ReportType, Name } = report;
        const params = { reportId: ReportId, reportType: ReportType, reportName: Name.Name };
        return getRoutePath(REPORT_EXECUTE, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const executeReportAction = (report) => {
        if (report.HasParameters) {
            const path = getExecuteReportPath(report);
            goToPath(path);
        } else {
            setOpenedModalData(report);
            setIsExecuteModalOpenedState(true);
        }
    };

    const getScheduleReportPath = (report) => {
        const { ReportId, ReportType, Name } = report;
        const params = { reportId: ReportId, reportType: ReportType, reportName: Name.Name };
        return getRoutePath(REPORT_SCHEDULE, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const stopScheduleReportAction = (report) => {
        setOpenedModalData(report);
        setIsStopScheduleModalOpenedState(true);
    };

    const infoReportAction = (report) => {
        setOpenedModalData({ ...report, isViewMode });
        setIsInfoModalOpenedState(true);
    };

    useImperativeHandle(modalActionsRef, () => ({
        onInfoModalOpen: infoReportAction,
    }));

    const getReportVersionListPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId, reportType: ReportType };
        return getRoutePath(REPORT_EXECUTED_LIST, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const getCloneReportPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId };

        const route =
            ReportType === ReportTypesLabels.Report
                ? REPORT_CLONE
                : ReportType === ReportTypesLabels.CompositeReport
                  ? REPORT_COMPOSITE_CLONE
                  : REPORT_CUSTOMER_JOURNEY_CLONE;

        return getRoutePath(route, params);
    };

    const editReportAction = (report) => {
        doPostRequest(getReportsByReport.request, {
            requestBody: {
                reportId: report.ReportId,
                reportType: report.ReportType,
            },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditReport(report);
                } else {
                    setOpenedModalData({ objects: Data.map(({ Name, Type }) => ({ Name, Type })), report });
                    setIsUsedInConfirmationModalOpenedState(true);
                }
            },
        });
    };

    const getClonePredefinedPath = ({ ReportId }) => {
        const params = { reportId: ReportId };

        return getRoutePath(REPORT_PREDEFINED_CLONE, params);
    };

    const archiveReportAction = (report) => {
        setOpenedModalData(report);
        setIsArchiveModalOpenedState(true);
    };

    const unarchiveReportAction = (report) => {
        setOpenedModalData(report);
        setIsUnarchiveModalOpenedState(true);
    };

    const modalsStateSetDict = {
        [modalsStateSetDictKeys.deleteReport]: {
            key: 'deleteReport',
            fn: setIsDeleteModalOpenedState,
        },
        [modalsStateSetDictKeys.stopScheduleReport]: {
            key: 'stopScheduleReport',
            fn: setIsStopScheduleModalOpenedState,
        },
        [modalsStateSetDictKeys.infoReport]: {
            key: 'infoReport',
            fn: setIsInfoModalOpenedState,
        },
        [modalsStateSetDictKeys.executeReport]: {
            key: 'executeReport',
            fn: setIsExecuteModalOpenedState,
        },
        [modalsStateSetDictKeys.editReport]: {
            key: 'editReport',
            fn: setIsUsedInConfirmationModalOpenedState,
        },
        [modalsStateSetDictKeys.archiveReport]: {
            key: 'archiveReport',
            fn: setIsArchiveModalOpenedState,
        },
        [modalsStateSetDictKeys.unarchiveReport]: {
            key: 'unarchiveReport',
            fn: setIsUnarchiveModalOpenedState,
        },
    };

    const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage, count = 1) => {
        if (
            action === ModalsActions.UNSCHEDULE_REPORT ||
            action === ModalsActions.DELETE ||
            action === ModalsActions.EXECUTE_REPORT ||
            action === ModalsActions.ARCHIVE ||
            (action === ModalsActions.UNARCHIVE && modalStateKey !== modalsStateSetDict.infoReport.key)
        ) {
            dataTableRef.current?.resetSelectedRows?.();
            dataTableRef.current?.changePaginationPageNumber?.(
                getPageNumber(
                    baseRequestBody.Filters,
                    action,
                    reportListDataCount,
                    baseRequestBody.Pageing.PageSize,
                    baseRequestBody.Pageing.PageNumber,
                    count,
                ),
            );
            getTableRefreshFn();
        }
        showToaster(alertType, alertMessage);

        if (action === ModalsActions.SCHEDULE_REPORT && alertType === AlertTypes.danger) {
            modalsStateSetDict[modalStateKey].fn(true);
        } else {
            modalsStateSetDict[modalStateKey].fn(false);
        }
    };

    const handleInfoModalActionClick = (modalKey, data) => {
        setIsInfoModalOpenedState(false);
        if (!isNil(modalKey) && !isNil(data)) {
            setOpenedModalData(data);
            modalsStateSetDict[modalKey].fn(true);
        }
    };

    const getRowActionBar = (row) => {
        const { data } = row;

        return (
            <>
                <ReportActionsButtons
                    data={data}
                    isInfoVisible={true}
                    getReportVersionListPath={getReportVersionListPath}
                    getScheduleReportPath={getScheduleReportPath}
                    stopScheduleReportAction={stopScheduleReportAction}
                    deleteReportAction={deleteReportAction}
                    getCloneReportPath={getCloneReportPath}
                    editReportAction={editReportAction}
                    getEditReportPath={getEditReportPath}
                    infoReportAction={infoReportAction}
                    executeReportAction={executeReportAction}
                    getExecuteReportPath={getExecuteReportPath}
                    archiveReportAction={archiveReportAction}
                    unarchiveReportAction={unarchiveReportAction}
                    isViewMode={isViewMode}
                    getClonePredefinedPath={getClonePredefinedPath}
                />
            </>
        );
    };

    const getTableColumns = () => {
        return columns.map((item) => ({ ...item, sortable: item.sortable && dragDisable }));
    };

    const getTableRows = (reportList) => {
        return reportList.map(
            ({
                Name,
                State,
                CreatorName,
                ModifierName,
                ReportType,
                ReportId,
                CreatedDate,
                ModifiedDate,
                CategoryId,
                CategoryName,
                CategoryColor,
                HasParameters,
                CompositeExecutionCount,
                SelfExecutionCount,
                IsUsed,
                ArchivedDate,
                IsExported,
            }) => {
                return {
                    hasHover: true,
                    dragDisable: dragDisable,
                    nestedData: [],
                    className: !isNil(ArchivedDate) ? 'crm-data-table-row-archive' : '',
                    data: {
                        rowKey: `${ReportId}${ReportType}`,
                        isRowOpend: false,
                        State,
                        Name,
                        'CreatedBy.Name': CreatorName,
                        'ModifiedBy.Name': ModifierName,
                        ReportType,
                        ReportId,
                        CreatedDate,
                        ModifiedDate,
                        CategoryId,
                        CategoryName,
                        CategoryColor,
                        HasParameters,
                        SelfExecutionCount,
                        CompositeExecutionCount,
                        IsUsed,
                        ArchivedDate,
                        IsExported,
                        isViewMode,
                    },
                };
            },
        );
    };

    const getTableRefreshFn = () => {
        getReports(baseRequestBody);
    };

    const getDataTableName = () => <span>{t(l.Reports)}</span>;

    const isAllow = (report) => {
        const { ReportType, IsUsed, ArchivedDate, IsExported } = report;
        const isArchive = !isNil(ArchivedDate);

        return {
            delete: ReportType !== ReportTypesLabels.CustomReport && !IsUsed && !IsExported,
            archive: !isArchive,
            unarchive: isArchive,
        };
    };

    useImperativeHandle(ref, () => ({
        resetSelectedRows() {
            dataTableRef.current?.resetSelectedRows?.();
        },
        reset() {
            dataTableRef.current?.reset?.();
        },
    }));

    useEffect(() => {
        if (isMounted.current) {
            getReports(baseRequestBody);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [baseRequestBody]);

    const getReportsNamesIds = (selectedRows) => {
        if (!isArray(selectedRows)) return;

        interface ReportItem {
            reportId: number;
            name: string;
            reportType: string;
        }

        const allowDeleteReports: ReportItem[] = [];
        const notAllowDeleteReports: ReportItem[] = [];
        const allowArchiveReports: ReportItem[] = [];
        const notAllowArchiveReports: ReportItem[] = [];
        const allowUnarchiveReports: ReportItem[] = [];
        const notAllowUnarchiveReports: ReportItem[] = [];

        selectedRows.forEach(({ data }) => {
            const { ReportId, Name, ReportType } = data;
            const isAllowData = isAllow(data);
            if (isAllowData.delete) {
                allowDeleteReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            }

            if (!isAllowData.delete) {
                notAllowDeleteReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            }
            if (isAllowData.archive) {
                allowArchiveReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            } else {
                notAllowArchiveReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            }

            if (isAllowData.unarchive) {
                allowUnarchiveReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            } else {
                notAllowUnarchiveReports.push({ reportId: ReportId, name: Name, reportType: ReportType });
            }
        });

        return {
            allowDeleteReports: allowDeleteReports,
            notAllowDeleteReports: notAllowDeleteReports,
            allowArchiveReports: allowArchiveReports,
            notAllowArchiveReports: notAllowArchiveReports,
            allowUnarchiveReports: allowUnarchiveReports,
            notAllowUnarchiveReports: notAllowUnarchiveReports,
        };
    };

    const deleteSelectedRows = (selectedRows) => {
        const namesIds = {
            allowDeleteReports: getReportsNamesIds(selectedRows)?.allowDeleteReports,
            notAllowDeleteReports: getReportsNamesIds(selectedRows)?.notAllowDeleteReports,
        };

        deleteReportAction(namesIds);
    };

    const archiveSelectedRows = (selectedRows) => {
        const reportNamesIds = getReportsNamesIds(selectedRows);
        const namesIds = {
            allowArchiveReports: reportNamesIds?.allowArchiveReports,
            notAllowArchiveReports: reportNamesIds?.notAllowArchiveReports,
        };
        archiveReportAction(namesIds);
    };

    const unarchiveSelectedRows = (selectedRows) => {
        const reportNamesIds = getReportsNamesIds(selectedRows);
        const namesIds = {
            allowUnarchiveReports: reportNamesIds?.allowUnarchiveReports,
            notAllowUnarchiveReports: reportNamesIds?.notAllowUnarchiveReports,
        };
        unarchiveReportAction(namesIds);
    };

    return (
        <>
            <DataTable
                classNames={dataTableClassNames}
                getTitle={getTitle}
                rowKey="rowKey"
                columnKey="dataKey"
                name={getDataTableName()}
                tableKey={tableKey}
                isColumnsSortable={true}
                withPagination={true}
                withManageLabels={withManageLabels}
                setLabelObjectIdKey={'ReportId'}
                labelNameKey={'CategoryName'}
                labelColorKey={'CategoryColor'}
                labelObjectType={TargetTypeEnum.Report}
                data={reportListData}
                dataCount={reportListDataCount}
                rowActionBar={getRowActionBar}
                isRowExpandable={false}
                columns={getTableColumns()}
                isLoading={isReportListLoading}
                isShowRefreshButton={true}
                isShowVerticalDots={isShowVerticalDots}
                isShowColumnChooser={isShowColumnChooser}
                setPagingPageSize={changePageSize}
                setPagingPageNumber={changePageNumber}
                sorting={dragDisable ? baseRequestBody.Sorting : {}}
                setSorting={changeSorting}
                onRefreshClick={getTableRefreshFn}
                headerActions={isNil(headerConfig) ? getHeaderActions() : undefined}
                headerConfig={headerConfig}
                isHasLeftHeaderActions={isHasLeftHeaderActions}
                headerLeftActions={headerLeftActions}
                isHasRowMultiSelect={isHasRowMultiSelect}
                isHasBulkActions={isHasBulkActions}
                archiveSelectedRows={archiveSelectedRows}
                unarchiveSelectedRows={unarchiveSelectedRows}
                deleteSelectedRows={deleteSelectedRows}
                receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                withQueryParams={withQueryParams}
                isHasRowRadioSelect={isHasRowRadioSelect}
                getRadioSelectedRow={getRadioSelectedRow}
                defaultRadioSelectedRow={defaultRadioSelectedRow}
                getDragAcceptType={getDragAcceptType}
                CustomPreview={CustomPreview}
                ref={dataTableRef}
            />

            <ReportActionsModals
                isDeleteModalOpenedState={isDeleteModalOpenedState}
                isStopScheduleModalOpenedState={isStopScheduleModalOpenedState}
                isInfoModalOpenedState={isInfoModalOpenedState}
                isExecuteModalOpenedState={isExecuteModalOpenedState}
                isUsedInConfirmationModalOpenedState={isUsedInConfirmationModalOpenedState}
                isArchiveModalOpenedState={isArchiveModalOpenedState}
                isUnarchiveModalOpenedState={isUnarchiveModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
                goToEditReport={goToEditReport}
                handleInfoModalActionClick={handleInfoModalActionClick}
            />
        </>
    );
});

ReportList.displayName = 'ReportList';

export default memo(ReportList);
